import { Component, ElementRef, NgZone } from '@angular/core';
import { HeroMotifBase, RGB } from '../hero-motif/hero-motif.base';

// A firefly: where it hovers, how it wanders, and when it glows.
interface Firefly {
  x: number;
  y: number;
  /** How far it has flown from its own hovering place, and how fast (it flies off from the pointer and back). */
  ox: number;
  oy: number;
  vx: number;
  vy: number;
  ax: number;
  ay: number;
  fx: number;
  fy: number;
  phase: number;
  blink: number;
  /** Which comes out first: at rest only the low ranks glow, awake all of them. */
  rank: number;
  /** How far it has come out (0..1, eased, so it swells in and fades away). */
  shown: number;
  /** One that came out round the pointer, and whether it is going again. */
  visitor?: boolean;
  leaving?: boolean;
  /** Seconds a visitor still has before it fades away. */
  life?: number;
  size: number;
  tint: number;
}

const LOOP = 40;
const W0 = Math.PI * 2 / LOOP;

/**
 * Sequoia's hero board (owner, 2026-10-16: a sequoia grove as the photographs show it, at dusk, with
 * fireflies): giant cinnamon trunks frame the stage at both sides, the grove going on into the haze
 * down the corridor between them, the floor warm with fallen needles; fireflies drift about, glowing
 * on and off in the colour circle's gold and green, each swelling in slowly; new ones come out
 * round the pointer and fade away when it has gone.
 */
@Component( {
  selector: 'app-hero-sequoia',
  template: '<canvas #canvas class="hq-canvas" aria-hidden="true"></canvas>',
  styles: [ ':host { display: block; position: absolute; inset: 0; overflow: hidden; pointer-events: none; }',
    '.hq-canvas { display: block; position: absolute; inset: 0; }' ],
} )
export class HeroSequoiaComponent extends HeroMotifBase {

  private flies: Firefly[] = [];
  private visitors: Firefly[] = [];
  private spawn = 0;
  private leave = 0;
  private scene: HTMLCanvasElement | null = null;
  private back: HTMLCanvasElement | null = null;
  private mist: HTMLCanvasElement | null = null;
  private glows: HTMLCanvasElement[] = [];
  private seedScene = 1;
  private scaleK = 1;

  constructor( zone: NgZone, host: ElementRef<HTMLElement> ) {
    super( zone, host );
    this.loop = LOOP;
    this.ease = 1.4;
  }

  protected colorsChanged(): void {
    this.scene = null;
    this.back = null;
    this.mist = null;
    this.glows = [];
  }

  protected speed(): number {
    return 0.6 + 0.6 * this.energy;
  }

  protected build( rand: () => number ): void {
    const W = this.width;
    const H = this.height;
    this.scaleK = Math.max( 0.55, Math.min( 1.25, H / 760 ) );
    this.seedScene = Math.floor( rand() * 1e6 );
    this.scene = null;
    const count = Math.round( W * H / 12000 );
    this.flies = [];
    for ( let i = 0; i < count; i++ ) {
      // most of them low, over the roots and the ferns, a few up the trunk
      // spread up the trunk too, not only round its middle, a little room left at the top (owner)
      let y = H * ( 0.12 + 0.72 * rand() );
      let x = rand() * W;
      // few behind the logo (owner): most that land there are moved off it
      const logo = this.blocks[ 0 ];
      for ( let tries = 0; logo && tries < 4 && rand() < 0.85
        && x > logo.x - 60 && x < logo.x + logo.w + 60 && y > logo.y - 50 && y < logo.y + logo.h + 50; tries++ ) {
        x = rand() * W;
        y = H * ( 0.12 + 0.72 * rand() );
      }
      this.flies.push( {
        x, y, ox: 0, oy: 0, vx: 0, vy: 0, ax: ( 20 + rand() * 50 ) * this.scaleK, ay: ( 10 + rand() * 30 ) * this.scaleK,
        fx: 1 + Math.floor( rand() * 3 ), fy: 1 + Math.floor( rand() * 3 ), phase: rand() * Math.PI * 2,
        blink: 2 + Math.floor( rand() * 4 ), rank: rand(), shown: 0, size: ( 26 + rand() * 20 ) * this.scaleK, tint: this.pickTint( rand() ),
      } );
    }
  }

  // ---- the drawing ----

  protected paint( ctx: CanvasRenderingContext2D, dt: number ): void {
    const scale = ctx.getTransform().a;
    if ( !this.frozen ) {
      if ( !this.scene ) { this.bakeScene( scale ); }
      if ( this.back ) { ctx.drawImage( this.back, 0, 0, this.width, this.height ); }
      if ( this.mist ) {
        // the fog drifts slowly to the right, wrapping round
        const off = ( performance.now() / 1000 * 9 * this.scaleK ) % this.width;
        ctx.drawImage( this.mist, off, 0, this.width, this.height );
        ctx.drawImage( this.mist, off - this.width, 0, this.width, this.height );
      }
      if ( this.scene ) { ctx.drawImage( this.scene, 0, 0, this.width, this.height ); }
    }
    if ( !this.glows.length ) { this.makeGlows(); }
    this.drawFlies( ctx, dt );
  }

  /** The fireflies: each wanders on its own small loop, and glows up and fades in its own rhythm. */
  private drawFlies( ctx: CanvasRenderingContext2D, dt: number ): void {
    const t = this.time;
    const e = this.energy;
    // the counter's charge brings the grove's own fireflies out; the pointer brings new ones where it is
    const charge = Math.max( 0, e - this.near );
    if ( dt > 0 ) { this.visit( dt ); }
    ctx.globalCompositeOperation = 'lighter';
    for ( const f of [ ...this.flies, ...this.visitors ] ) {
      const hx = f.x + f.ax * Math.sin( t * W0 * f.fx + f.phase );
      // whole turns in the loop on every wave, so nothing jumps when it comes round (owner)
      const hy = f.y + f.ay * Math.sin( t * W0 * ( f.fy + 1 ) + f.phase * 2 );
      // each one comes out slowly, its light swelling in, and goes the same way (owner: never all at once)
      // fewer at rest, once the counter has finished (owner)
      const want = f.visitor ? ( f.leaving ? 0 : 1 ) : ( f.rank <= 0.3 + 0.7 * charge ? 1 : 0 );
      // the pointer's own come out quicker (owner), every fade out stays slow
      const rate = f.visitor && want > f.shown ? 2.6 : 0.7;
      // a still frame (the page's wallpaper, reduced motion) has no time to swell in: show them at once
      f.shown = dt > 0 ? f.shown + ( want - f.shown ) * Math.min( 1, dt * rate ) : want;
      if ( f.shown < 0.01 ) { continue; }
      const pulse = Math.pow( Math.max( 0, Math.sin( t * W0 * f.blink + f.phase ) ), 3 );
      const glow = Math.min( 1, pulse * ( 0.8 + 0.3 * e ) + 0.2 + 0.15 * e ) * f.shown;
      if ( glow < 0.02 ) { continue; }
      ctx.globalAlpha = glow;
      const s = f.size * ( 0.8 + 0.25 * pulse );
      ctx.drawImage( this.glows[ f.tint ], hx - s / 2, hy - s / 2, s, s );
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  }

  /**
   * The pointer over the stage: new fireflies keep coming out round it, several a second, each
   * glowing a few seconds near where it came and fading away again; when the pointer has gone the
   * rest fade away too.
   */
  private visit( dt: number ): void {
    const k = this.scaleK;
    if ( this.near > 0.15 && this.pointerX > -1e3 ) {
      this.spawn += dt * 7;
      // no limit while the pointer stays: they keep coming, each living a few seconds (only a safety cap)
      while ( this.spawn >= 1 && this.visitors.length < 140 ) {
        this.spawn -= 1;
        const a = Math.random() * Math.PI * 2;
        const d = Math.sqrt( Math.random() ) * 130 * k;
        this.visitors.push( {
          x: this.pointerX + Math.cos( a ) * d, y: this.pointerY + Math.sin( a ) * d * 0.7,
          ox: 0, oy: 0, vx: 0, vy: 0, ax: ( 15 + Math.random() * 35 ) * k, ay: ( 8 + Math.random() * 20 ) * k,
          fx: 1 + Math.floor( Math.random() * 3 ), fy: 1 + Math.floor( Math.random() * 3 ), phase: Math.random() * Math.PI * 2,
          blink: 2 + Math.floor( Math.random() * 4 ), rank: 0, size: ( 26 + Math.random() * 20 ) * k,
          tint: this.pickTint( Math.random() ), shown: 0, visitor: true, leaving: false, life: 2.5 + Math.random() * 3,
        } );
      }
      if ( this.spawn > 1 ) { this.spawn = 1; }
    } else {
      // the pointer has gone: the oldest leave first, a few at a time
      this.leave += dt * 2.5;
      for ( const v of this.visitors ) {
        if ( this.leave < 1 ) { break; }
        if ( !v.leaving ) { v.leaving = true; this.leave -= 1; }
      }
      if ( this.leave > 1 ) { this.leave = 1; }
    }
    // each one glows a while and fades away on its own, the pointer there or not
    for ( const v of this.visitors ) {
      if ( v.life !== undefined ) {
        v.life -= dt;
        if ( v.life <= 0 ) { v.leaving = true; }
      }
    }
    this.visitors = this.visitors.filter( v => !( v.leaving && v.shown < 0.01 ) );
  }

  /** Yellow most often, then amber and orange, the brown less, the olive green rarely. */
  private pickTint( r: number ): number {
    return r < 0.32 ? 0 : r < 0.56 ? 1 : r < 0.78 ? 2 : r < 0.94 ? 3 : 4;
  }

  /**
   * A firefly's glow: a bright core and a soft halo, in the colours real fireflies glow (owner): the
   * yellows, ambers, oranges and an amber-brown, now and then an olive green; deep colours, never white.
   */
  private makeGlows(): void {
    // mostly yellows, ambers and oranges, a rare olive green (owner)
    const tones: RGB[] = [ [ 226, 192, 48 ], [ 212, 160, 40 ], [ 230, 132, 42 ], [ 186, 112, 44 ], [ 176, 180, 50 ] ];
    this.glows = tones.map( tone => {
      const c = document.createElement( 'canvas' );
      c.width = c.height = 64;
      const g = c.getContext( '2d' )!;
      const grad = g.createRadialGradient( 32, 32, 0, 32, 32, 32 );
      grad.addColorStop( 0, this.rgba( this.mix( tone, [ 255, 250, 200 ], 0.35 ), 1 ) );
      grad.addColorStop( 0.08, this.rgba( tone, 1 ) );
      grad.addColorStop( 0.3, this.rgba( tone, 0.35 ) );
      grad.addColorStop( 1, this.rgba( tone, 0 ) );
      g.fillStyle = grad;
      g.fillRect( 0, 0, 64, 64 );
      return c;
    } );
  }

  /**
   * The grove at night, painted once and kept soft (owner, 2026-10-16: the others had weight, this read
   * as a cartoon): no hard lines anywhere. The far grove is painted small and scaled up, so it comes out
   * blurred, as a camera at the foot of the giants would see it; fog lies between its depths. Two
   * giants rise at the sides, seen from their feet: wide where they stand, narrowing as they go up and
   * leaning in over the corridor. A few soft shafts of light fall down the corridor from the canopy,
   * and a bank of fog lies over the floor.
   */
  private bakeScene( scale: number ): void {
    const W = this.width;
    const H = this.height;
    const rand = this.seededScene( this.seedScene );
    const [ moss, green, gold, rose, earth ] = this.five();
    const night: RGB = [ 6, 8, 6 ];
    // darker still, the depths going to black (owner)
    // the background nearly black (owner), the fog only a breath of green
    const fog = this.mix( this.mix( moss, green, 0.15 ), night, 0.84 );
    const c = document.createElement( 'canvas' );
    c.width = Math.ceil( W * scale );
    c.height = Math.ceil( H * scale );
    const g = c.getContext( '2d' )!;
    g.scale( scale, scale );

    // the far grove, painted at a sixth of the size and scaled up: soft, out of focus, deep
    const lo = document.createElement( 'canvas' );
    const ls = 1 / 6;
    lo.width = Math.max( 2, Math.ceil( W * ls ) );
    lo.height = Math.max( 2, Math.ceil( H * ls ) );
    const l = lo.getContext( '2d' )!;
    l.scale( ls, ls );
    const sky = l.createLinearGradient( 0, 0, 0, H );
    sky.addColorStop( 0, this.rgba( night, 1 ) );
    sky.addColorStop( 0.7, this.rgba( fog, 1 ) );
    sky.addColorStop( 1, this.rgba( this.mix( fog, night, 0.3 ), 1 ) );
    l.fillStyle = sky;
    l.fillRect( 0, 0, W, H );
    const glow = l.createRadialGradient( W / 2, H * 0.62, 0, W / 2, H * 0.62, H * 0.8 );
    glow.addColorStop( 0, this.rgba( this.mix( fog, gold, 0.12 ), 0.25 ) );
    glow.addColorStop( 1, this.rgba( fog, 0 ) );
    l.fillStyle = glow;
    l.fillRect( 0, 0, W, H );
    // behind, the silhouettes of other giants, the same flaring shape as the one in front, dark, the
    // farther ones smaller and sunk deeper into the black (owner)
    const cinnamon = this.mix( this.mix( rose, [ 150, 78, 42 ], 0.55 ), earth, 0.15 );
    // each one shaded round (a faint light down one side), its outline rough with bark, a few
    // fluting shadows down it
    const giantShape = ( cx: number, half: number, base: number, tone: RGB ) => {
      const ph = rand() * 6.28;
      const halfAt = ( y: number, side: number ) => half * ( 0.6 + 0.4 * Math.pow( Math.max( 0, y / base ), 3.2 ) ) * ( 1 - 0.14 * ( 1 - y / base ) )
        + ( Math.sin( y * 0.006 + ph + side ) * 0.05 + Math.sin( y * 0.02 + ph * 2 + side ) * 0.02 ) * half;
      const path = () => {
        l.beginPath();
        l.moveTo( cx - halfAt( 0, -1 ), -10 );
        for ( let y = 0; y <= base; y += 10 ) { l.lineTo( cx - halfAt( y, -1 ), y ); }
        for ( let y = base; y >= 0; y -= 10 ) { l.lineTo( cx + halfAt( y, 1 ), y ); }
        l.lineTo( cx + halfAt( 0, 1 ), -10 );
        l.closePath();
      };
      path();
      const sg = l.createLinearGradient( cx - half * 1.2, 0, cx + half * 1.2, 0 );
      sg.addColorStop( 0, this.rgba( this.mix( tone, night, 0.5 ), 1 ) );
      sg.addColorStop( 0.6, this.rgba( tone, 1 ) );
      sg.addColorStop( 0.9, this.rgba( this.mix( tone, cinnamon, 0.12 ), 1 ) );
      sg.addColorStop( 1, this.rgba( this.mix( tone, night, 0.3 ), 1 ) );
      l.fillStyle = sg;
      l.fill();
      l.save();
      path();
      l.clip();
      for ( let f = 0; f < 6; f++ ) {
        const u = -0.8 + 1.6 * ( f + rand() * 0.6 ) / 6;
        l.strokeStyle = this.rgba( this.mix( tone, night, 0.55 ), 0.5 );
        l.lineWidth = half * ( 0.05 + rand() * 0.06 );
        l.beginPath();
        for ( let y = 0; y <= base; y += 20 ) {
          const x = cx + u * halfAt( y, u ) + Math.sin( y * 0.005 + ph * 3 + f ) * half * 0.04;
          if ( y === 0 ) { l.moveTo( x, y ); } else { l.lineTo( x, y ); }
        }
        l.stroke();
      }
      l.restore();
    };
    for ( let depth = 0; depth < 3; depth++ ) {
      const n = 3 + depth;
      // a touch lighter than before (owner), still well back in the dark
      const tone = this.mix( cinnamon, night, 0.86 - depth * 0.04 );
      for ( let i = 0; i < n; i++ ) {
        const x = W * ( ( i + 0.5 + ( rand() - 0.5 ) * 0.6 ) / n );
        giantShape( x, H * ( 0.1 + depth * 0.07 ) * ( 0.8 + rand() * 0.4 ), H * ( 0.8 + depth * 0.03 ), tone );
      }
      const mist = l.createLinearGradient( 0, 0, 0, H );
      mist.addColorStop( 0, this.rgba( fog, 0.08 ) );
      mist.addColorStop( 0.8, this.rgba( fog, 0.3 ) );
      mist.addColorStop( 1, this.rgba( fog, 0.15 ) );
      l.fillStyle = mist;
      l.fillRect( 0, 0, W, H );
    }
    // fog among the far giants (owner): long soft banks of pale mist drifting between their trunks
    // a dark green mist, between the forest's green and black (owner), not grey-blue
    // its green muted toward grey (owner)
    const mistTone = this.mix( this.mix( this.mix( moss, green, 0.3 ), [ 72, 76, 70 ], 0.5 ), night, 0.74 );
    for ( let i = 0; i < 26; i++ ) {
      const x = rand() * W;
      const y = H * ( 0.36 + rand() * 0.46 );
      const rx = W * ( 0.15 + rand() * 0.25 );
      const ry = H * ( 0.04 + rand() * 0.06 );
      l.save();
      l.translate( x, y );
      l.scale( rx, ry );
      const mg = l.createRadialGradient( 0, 0, 0, 0, 0, 1 );
      mg.addColorStop( 0, this.rgba( mistTone, 0.3 ) );
      mg.addColorStop( 1, this.rgba( mistTone, 0 ) );
      l.fillStyle = mg;
      l.fillRect( -1, -1, 2, 2 );
      l.restore();
    }
    // the floor, far and soft
    const floor = l.createLinearGradient( 0, H * 0.72, 0, H );
    // the floor in the theme's green, mossy, dark, rising out of the fog with no edge
    floor.addColorStop( 0, this.rgba( this.mix( this.mix( moss, green, 0.25 ), fog, 0.55 ), 0 ) );
    floor.addColorStop( 0.35, this.rgba( this.mix( this.mix( moss, green, 0.25 ), fog, 0.55 ), 1 ) );
    floor.addColorStop( 1, this.rgba( this.mix( this.mix( moss, green, 0.2 ), night, 0.68 ), 1 ) );
    l.fillStyle = floor;
    l.fillRect( 0, H * 0.72, W, H );
    g.imageSmoothingQuality = 'high';
    // only the painted part of the small canvas (its last, partly painted column came out as a dark
    // broken line down the right edge)
    g.drawImage( lo, 0, 0, Math.floor( W * ls ), Math.floor( H * ls ), 0, 0, W, H );

    // the shafts of light falling down the corridor from the canopy, faint and warm
    g.save();
    g.globalCompositeOperation = 'lighter';
    for ( let i = 0; i < 4; i++ ) {
      const x = W * ( 0.42 + rand() * 0.2 );
      const w = H * ( 0.04 + rand() * 0.06 );
      const lean = H * ( 0.12 + rand() * 0.1 );
      const beam = g.createLinearGradient( 0, 0, 0, H * 0.85 );
      beam.addColorStop( 0, this.rgba( this.mix( gold, [ 255, 230, 180 ], 0.4 ), 0.045 ) );
      beam.addColorStop( 1, this.rgba( gold, 0 ) );
      g.fillStyle = beam;
      g.beginPath();
      g.moveTo( x, -10 );
      g.lineTo( x + w, -10 );
      g.lineTo( x + w * 2.2 - lean, H * 0.85 );
      g.lineTo( x - w * 0.8 - lean, H * 0.85 );
      g.closePath();
      g.fill();
    }
    g.restore();

    this.back = c;
    // in front, on its own layer so the drifting fog can pass between the far grove and the giant
    const front = document.createElement( 'canvas' );
    front.width = c.width;
    front.height = c.height;
    const gf = front.getContext( '2d' )!;
    gf.scale( scale, scale );

    // the giant, seen from its foot
    // one giant, in the middle (owner), standing straight, lit a little from its right
    this.drawGiant( gf, W / 2, Math.min( W * 0.34, H * 0.62 ), 0, 1, rand, scale );

    // the undergrowth: blades of grass and low growth in the forest's dark green, rising from the floor
    // round the giant's foot, the far ones short and lost in the fog, the near ones tall and dark
    const [ mossG, greenG ] = [ moss, green ];
    for ( let i = 0; i < 3600; i++ ) {
      const near = Math.pow( rand(), 0.6 );
      const y = H * ( 0.8 + 0.22 * near );
      const x = rand() * W;
      const h = H * ( 0.012 + 0.05 * near * ( 0.5 + rand() ) );
      const lean = ( rand() - 0.5 ) * h * 0.9;
      const tone = this.mix( this.mix( mossG, greenG, rand() * 0.3 ), night, 0.35 + ( 1 - near ) * 0.25 + rand() * 0.2 );
      gf.strokeStyle = this.rgba( this.mix( tone, fog, ( 1 - near ) * 0.5 ), 0.9 );
      gf.lineWidth = ( 0.8 + near * 1.8 ) * this.scaleK;
      gf.lineCap = 'round';
      gf.beginPath();
      gf.moveTo( x, y );
      gf.quadraticCurveTo( x + lean * 0.3, y - h * 0.6, x + lean, y - h );
      gf.stroke();
    }

    // the fog over the floor, and the night closing in at the edges
    const bank = gf.createLinearGradient( 0, H * 0.72, 0, H );
    bank.addColorStop( 0, this.rgba( fog, 0 ) );
    bank.addColorStop( 0.6, this.rgba( this.mix( this.mix( moss, green, 0.2 ), fog, 0.5 ), 0.3 ) );
    bank.addColorStop( 1, this.rgba( this.mix( moss, night, 0.7 ), 0.65 ) );
    gf.fillStyle = bank;
    gf.fillRect( 0, H * 0.72, W, H );
    const vig = gf.createRadialGradient( W / 2, H * 0.55, Math.min( W, H ) * 0.35, W / 2, H * 0.55, Math.max( W, H ) * 0.8 );
    vig.addColorStop( 0, this.rgba( night, 0 ) );
    vig.addColorStop( 1, this.rgba( night, 0.75 ) );
    gf.fillStyle = vig;
    gf.fillRect( 0, 0, W, H );
    this.scene = front;
    this.bakeMist( scale );
  }

  /**
   * The fog that drifts through the grove (owner: the fog was not doing what it should): low banks,
   * thickest near the ground, lying across the far giants' feet and passing behind the near one, on a
   * layer of its own that wraps round so it can drift on for ever. Dark, its green muted to grey.
   */
  private bakeMist( scale: number ): void {
    const W = this.width;
    const H = this.height;
    const [ moss, green ] = this.five();
    const night: RGB = [ 6, 8, 6 ];
    const tone = this.mix( this.mix( this.mix( moss, green, 0.3 ), [ 80, 84, 78 ], 0.5 ), night, 0.6 );
    const rand = this.seededScene( this.seedScene + 7 );
    // painted small and scaled up, so the banks are soft
    const ms = 1 / 4;
    const m = document.createElement( 'canvas' );
    m.width = Math.max( 2, Math.ceil( W * ms ) );
    m.height = Math.max( 2, Math.ceil( H * ms ) );
    const g = m.getContext( '2d' )!;
    g.scale( ms, ms );
    for ( let i = 0; i < 34; i++ ) {
      const x = rand() * W;
      // most of it low, a few thinner wisps higher up
      const y = H * ( 0.5 + 0.42 * Math.pow( rand(), 0.6 ) );
      const rx = W * ( 0.12 + rand() * 0.22 );
      const ry = H * ( 0.035 + rand() * 0.06 );
      const a = 0.22 + 0.3 * ( ( y / H ) - 0.5 ) / 0.42;
      for ( const dx of [ -W, 0, W ] ) {
        g.save();
        g.translate( x + dx, y );
        g.scale( rx, ry );
        const mg = g.createRadialGradient( 0, 0, 0, 0, 0, 1 );
        mg.addColorStop( 0, this.rgba( tone, a ) );
        mg.addColorStop( 1, this.rgba( tone, 0 ) );
        g.fillStyle = mg;
        g.fillRect( -1, -1, 2, 2 );
        g.restore();
      }
    }
    const c = document.createElement( 'canvas' );
    c.width = Math.ceil( W * scale );
    c.height = Math.ceil( H * scale );
    c.getContext( '2d' )!.drawImage( m, 0, 0, Math.floor( W * ms ), Math.floor( H * ms ), 0, 0, c.width, c.height );
    this.mist = c;
  }

  /**
   * A giant, seen from its feet: its foot wide and buttressed at the bottom of the frame, narrowing as
   * it rises and leaning in over the corridor, out of the frame above. Its bark is a dark cinnamon,
   * modelled softly in broad vertical flutes (each a blurred dark crease and a faint light ridge, no
   * line ever sharp), lit at the edge that faces the corridor and lost in shadow on the other side.
   */
  private drawGiant( g: CanvasRenderingContext2D, footX: number, footHalf: number, inward: number, light: number, rand: () => number, scale: number ): void {
    const H = this.height;
    const W = this.width;
    const k = this.scaleK;
    const [ , , gold, rose, earth ] = this.five();
    const night: RGB = [ 10, 12, 10 ];
    // a touch darker and a touch redder (owner)
    const cinnamon = this.mix( this.mix( rose, [ 160, 70, 40 ], 0.62 ), earth, 0.12 );
    const bark = this.mix( cinnamon, night, 0.58 );
    const lit = this.mix( cinnamon, this.mix( gold, [ 240, 170, 100 ], 0.4 ), 0.3 );
    const dark = this.mix( cinnamon, night, 0.82 );
    const base = H * 1.02;
    // the centre leans in as the trunk rises; the half width narrows going up, flaring hard at the foot
    const centre = ( y: number ) => footX + inward * ( 1 - y / base ) * H * 0.16;
    // it narrows all the way up as a real sequoia does, flaring hard only at the foot (owner)
    const halfAt = ( y: number ) => {
      // kept within 0..1: above the frame it went past 1 and the power gave NaN, leaving a line at the top
      const up = Math.min( 1, Math.max( 0, 1 - y / base ) );
      // steep sides (owner): it narrows, but stands upright, the flare kept low at the foot
      return footHalf * ( 0.6 + 0.4 * Math.pow( 1 - up, 3.2 ) ) * ( 1 - 0.14 * up );
    };
    // the outline is never a straight line: bark ridges bulge and dip along it, at several scales
    const bumps = [ 0, 1, 2, 3, 4, 5 ].map( () => rand() * 6.28 );
    const rough = ( y: number, side: number ) => {
      const o = side > 0 ? 0 : 3;
      return ( Math.sin( y * 0.0035 + bumps[ o ] ) * 9 + Math.sin( y * 0.011 + bumps[ o + 1 ] ) * 5
        + Math.sin( y * 0.033 + bumps[ o + 2 ] ) * 2.2 ) * k;
    };
    const edge = ( y: number, side: number ) => centre( y ) + side * ( halfAt( y ) + rough( y, side ) );
    const outline = () => {
      g.beginPath();
      g.moveTo( edge( -20, -1 ), -20 );
      for ( let y = -20; y <= base; y += 8 ) { g.lineTo( edge( y, -1 ), y ); }
      for ( let y = base; y >= -20; y -= 8 ) { g.lineTo( edge( y, 1 ), y ); }
      g.closePath();
    };
    const fx0 = footX - footHalf * 1.2;
    const fx1 = footX + footHalf * 1.2;
    outline();
    const body = g.createLinearGradient( light > 0 ? fx0 : fx1, 0, light > 0 ? fx1 : fx0, 0 );
    body.addColorStop( 0, this.rgba( this.mix( dark, night, 0.4 ), 1 ) );
    body.addColorStop( 0.55, this.rgba( this.mix( bark, dark, 0.5 ), 1 ) );
    body.addColorStop( 0.86, this.rgba( bark, 1 ) );
    body.addColorStop( 0.97, this.rgba( this.mix( bark, lit, 0.7 ), 1 ) );
    body.addColorStop( 1, this.rgba( bark, 1 ) );
    g.fillStyle = body;
    g.fill();
    g.save();
    outline();
    g.clip();
    // the flutes, modelled softly: blurred creases and faint ridges, each wandering, swelling and
    // thinning along its run, breaking off and starting again, never a ruled line (owner)
    const along = ( u: number, y: number, ph: number ) => centre( y ) + u * halfAt( y )
      + ( Math.sin( y * 0.004 + ph ) * 8 + Math.sin( y * 0.013 + ph * 2 ) * 3 + Math.sin( y * 0.041 + ph * 3 ) * 1.2 ) * k;
    const crease = ( u: number, ph: number, y0: number, y1: number, w0: number, colour: RGB, alpha: number, blur: number, du: number ) => {
      g.save();
      g.shadowColor = this.rgba( colour, Math.min( 1, alpha * 2 ) );
      g.shadowBlur = blur * k * scale;
      g.strokeStyle = this.rgba( colour, alpha );
      g.lineCap = 'round';
      // one smooth stroke per run (segment by segment it came out dotted at the joints)
      g.lineWidth = w0;
      g.lineJoin = 'round';
      g.beginPath();
      for ( let y = y0; y <= y1; y += 14 ) {
        const x = along( u + du, y, ph );
        if ( y === y0 ) { g.moveTo( x, y ); } else { g.lineTo( x, y ); }
      }
      g.stroke();
      g.restore();
    };
    for ( let i = 0; i < 20; i++ ) {
      const u = -0.97 + 1.94 * ( i + 0.5 + ( rand() - 0.5 ) * 0.7 ) / 20;
      const ph = rand() * 6.28;
      // broken into two or three runs down the trunk
      let y = -20;
      while ( y < base ) {
        const len = H * ( 0.25 + rand() * 0.5 );
        const w = ( 6 + rand() * 12 ) * k;
        crease( u, ph, y, Math.min( base, y + len ), w, this.mix( dark, night, 0.5 ), 0.32, 16, 0 );
        crease( u, ph, y, Math.min( base, y + len ), w * 0.55, this.mix( bark, lit, 0.55 ), 0.09, 10, light * 0.05 );
        y += len + H * ( 0.02 + rand() * 0.06 );
      }
    }
    // the bark's patches: worn lighter places and darker ones, soft
    for ( let i = 0; i < 26; i++ ) {
      const y = rand() * base;
      const x = centre( y ) + ( rand() * 2 - 1 ) * halfAt( y ) * 0.9;
      const r = ( 20 + rand() * 60 ) * k;
      const lighter = rand() < 0.5;
      const pg = g.createRadialGradient( x, y, 0, x, y, r );
      pg.addColorStop( 0, this.rgba( lighter ? this.mix( bark, lit, 0.5 ) : this.mix( dark, night, 0.4 ), lighter ? 0.12 : 0.18 ) );
      pg.addColorStop( 1, this.rgba( bark, 0 ) );
      g.fillStyle = pg;
      g.save();
      g.translate( x, y );
      g.scale( 0.45, 1.6 );
      g.translate( -x, -y );
      g.fillRect( x - r, y - r, r * 2, r * 2 );
      g.restore();
    }
    // a few burls: rounded swellings, lit above and dark beneath
    for ( let i = 0; i < 3; i++ ) {
      const y = H * ( 0.15 + rand() * 0.55 );
      const x = centre( y ) + ( rand() * 1.6 - 0.8 ) * halfAt( y );
      const r = ( 10 + rand() * 18 ) * k;
      const bg = g.createRadialGradient( x + light * r * 0.25, y - r * 0.35, 0, x, y, r );
      bg.addColorStop( 0, this.rgba( this.mix( bark, lit, 0.6 ), 0.5 ) );
      bg.addColorStop( 0.6, this.rgba( bark, 0.3 ) );
      bg.addColorStop( 1, this.rgba( dark, 0 ) );
      g.fillStyle = bg;
      g.beginPath();
      g.ellipse( x, y, r * 0.8, r, 0, 0, Math.PI * 2 );
      g.fill();
    }
    // the old fire scar: blackened bark up from the foot on one side
    {
      const side = -light;
      const sx = centre( base ) + side * halfAt( base ) * 0.45;
      const sg = g.createRadialGradient( sx, base, 0, sx, base, H * 0.42 );
      sg.addColorStop( 0, this.rgba( night, 0.6 ) );
      sg.addColorStop( 1, this.rgba( night, 0 ) );
      g.save();
      g.translate( sx, base );
      g.scale( 0.55, 1 );
      g.translate( -sx, -base );
      g.fillStyle = sg;
      g.fillRect( sx - H * 0.5, base - H * 0.5, H, H );
      g.restore();
    }
    // a fine grain, barely there
    for ( let i = 0; i < 500; i++ ) {
      const u = rand() * 2 - 1;
      const ph = rand() * 6.28;
      const y0 = rand() * H;
      const len = H * ( 0.05 + rand() * 0.2 );
      g.strokeStyle = this.rgba( rand() < 0.5 ? this.mix( bark, lit, 0.4 ) : this.mix( dark, night, 0.3 ), 0.04 + rand() * 0.06 );
      g.lineWidth = ( 1 + rand() * 1.5 ) * k;
      g.beginPath();
      for ( let y = y0; y < y0 + len; y += 10 ) { const x = along( u, y, ph ); if ( y === y0 ) { g.moveTo( x, y ); } else { g.lineTo( x, y ); } }
      g.stroke();
    }
    // the fog creeping onto the trunk low down, and its far side lost in shadow
    const low = g.createLinearGradient( 0, H * 0.7, 0, H );
    low.addColorStop( 0, this.rgba( night, 0 ) );
    low.addColorStop( 1, this.rgba( night, 0.35 ) );
    g.fillStyle = low;
    g.fillRect( Math.min( fx0, fx1 ) - 40, 0, Math.abs( fx1 - fx0 ) + 80 + H * 0.3, H );
    g.restore();
    void W;
  }

  private seededScene( seed: number ): () => number {
    let s = ( seed * 2654435761 ) >>> 0;
    return () => {
      s = ( s + 0x6D2B79F5 ) >>> 0;
      let t = Math.imul( s ^ ( s >>> 15 ), 1 | s );
      t = ( t + Math.imul( t ^ ( t >>> 7 ), 61 | t ) ) ^ t;
      return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
    };
  }

  /** The colour circle's five, or Sequoia's own: moss, green, gold, rose, earth. */
  private five(): RGB[] {
    if ( this.paletteColors.length >= 5 ) { return this.paletteColors.slice( 0, 5 ); }
    return [ [ 64, 81, 51 ], [ 110, 159, 86 ], [ 166, 143, 89 ], [ 152, 105, 105 ], [ 108, 98, 90 ] ];
  }
}
